import { useEffect, useState } from 'react';
import { api } from '../../services/api.js';
import type { Order, PoS, PoSDistributor, PosNote, Visit } from '../../types/index.js';

export interface VisitBriefingData {
  lastVisit: Visit | null;
  orders: Order[];
  distributors: PoSDistributor[];
  pinnedNotes: PosNote[];
  loading: boolean;
}

function isNotFound(err: unknown): boolean {
  return (err as { response?: { status?: number } }).response?.status === 404;
}

export function useVisitBriefing(posId: string | undefined): VisitBriefingData {
  const [lastVisit, setLastVisit] = useState<Visit | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [distributors, setDistributors] = useState<PoSDistributor[]>([]);
  const [pinnedNotes, setPinnedNotes] = useState<PosNote[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!posId) {
      setLastVisit(null);
      setOrders([]);
      setDistributors([]);
      setPinnedNotes([]);
      return;
    }

    let stale = false;
    setLoading(true);

    const load = async () => {
      const [visitRes, distRes, notesRes] = await Promise.allSettled([
        api.get<Visit>(`/visits/last/${posId}`),
        api.get<PoSDistributor[]>(`/pos/${posId}/distributors`),
        api.get<PosNote[]>(`/pos/${posId}/notes`),
      ]);
      if (stale) return;

      let visit: Visit | null = null;
      if (visitRes.status === 'fulfilled') visit = visitRes.value.data;
      else if (!isNotFound(visitRes.reason)) {
        console.error('Failed to load last visit', visitRes.reason);
      }
      setLastVisit(visit);

      setDistributors(
        distRes.status === 'fulfilled' ? distRes.value.data || [] : []
      );
      const notes =
        notesRes.status === 'fulfilled' ? notesRes.value.data || [] : [];
      setPinnedNotes(notes.filter((n) => n.is_pinned));

      if (visit?.id) {
        try {
          const orderRes = await api.get<Order[]>(`/orders?visit_id=${visit.id}`);
          if (!stale) setOrders(orderRes.data || []);
        } catch (err) {
          console.error('Failed to load last visit orders', err);
          if (!stale) setOrders([]);
        }
      } else {
        setOrders([]);
      }
      if (!stale) setLoading(false);
    };

    void load();
    return () => {
      stale = true;
    };
  }, [posId]);

  return { lastVisit, orders, distributors, pinnedNotes, loading };
}

export function posAddressLine(pos: PoS): string {
  const street = [pos.street, pos.street_number].filter(Boolean).join(' ');
  const city = [pos.postal_code, pos.city].filter(Boolean).join(' ');
  return [street, city].filter(Boolean).join(', ') || pos.address || '';
}
